import { type ReactNode } from "react";
import { TooltipProvider } from "@hulahoop/ui";
import { PostHogProvider } from "@posthog/react";
import posthog from "posthog-js";
import { AppSettingsProvider } from "@/contexts/AppSettingsContext";
import { PageTitleProvider } from "@/contexts/PageTitleContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { UserbackProvider } from "@/app/providers/UserbackProvider";
import { useSentryUserContext } from "@/app/useSentryUserContext";

interface ProvidersProps {
  children: ReactNode;
}

/**
 * Headless component that re-applies the Sentry user-context payload whenever
 * the authenticated user or active organization changes. Must live below
 * `AuthProvider` so it can read `AuthContext`.
 */
const SentryUserContextSync = () => {
  useSentryUserContext();
  return null;
};

export const Providers = ({ children }: ProvidersProps) => {
  return (
    <PostHogProvider client={posthog}>
      <AppSettingsProvider>
        <PageTitleProvider>
          <AuthProvider>
            <SentryUserContextSync />
            <UserbackProvider>
              <TooltipProvider>{children}</TooltipProvider>
            </UserbackProvider>
          </AuthProvider>
        </PageTitleProvider>
      </AppSettingsProvider>
    </PostHogProvider>
  );
};
